import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Layout from '~/components/Layout'
import { RouteTypes } from '~/routes/constants'
import DairyQuery from '~/QueryHooks/DairyQuery'
import { handleErrors } from '~/utils/helpers'
import DairyService from '~/api/services/dairy'
import notify from '~/components/Notify'
import { useNavigate, useParams } from 'react-router'
import { Form } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import LocationForm from '../Managers/CreateManager/LocationForm'

const CreateEditCollectionSites: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()

  const [isBusy, setIsBusy] = useState(false)

  const methods = useForm({
    mode: 'onSubmit'
  })

  const { control } = methods

  const {
    data: transportationAreasList,
    isLoading: isLoadingTransportAreas,
    refetch: fetchTransportList
  } = DairyQuery.getTransportationAreasList()

  const {
    data: collectionSiteOptions,
    isLoading: isLoadingCollectionOptions,
    refetch: refetchCollectionOptions
  } = DairyQuery.getCollectionPointsOptions()

  const {
    data: editData,
    isLoading: isLoadingEdit,
    refetch: refetchEdit
  } = DairyQuery.editCollectionSite(id)

  const {
    data: countries,
    isLoading: isLoadingCountries,
    refetch: fetchCountries
  } = DairyQuery.getCountriesList()

  useEffect(() => {
    fetchTransportList()
    refetchCollectionOptions()
    fetchCountries()
  }, [t])

  useEffect(() => {
    if (id) {
      refetchEdit()
    }
  }, [id, t])

  useEffect(() => {
    if (editData && id) {
      methods.reset({
        ...editData
      })
    }
  }, [editData, id])

  function renderForm() {
    return <>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          name={'uniqueId'}
          label={t('Collection site ID')}
          placeholder={t('Please enter Collection site ID')}
          rules={{
            required: t('Please enter Collection site ID'),
            maxLength: {
              value: 32,
              message: t('Max chart. is 32')
            }
          }}
        />
        <FormFields.Input
          control={control}
          name={'name'}
          label={t('Collection site name')}
          placeholder={t('Please enter Collection site name')}
          rules={{
            required: t('Please enter Collection site name'),
            maxLength: {
              value: 100,
              message: t('Max chart. is 100')
            }
          }}
        />
        <FormFields.Input
          control={control}
          name={'abbreviation'}
          label={t('Abbrevation')}
          placeholder={t('Please enter Abbrevation')}
          rules={{
            required: t('Please enter Abbrevation'),
            maxLength: {
              value: 100,
              message: t('Max chart. is 100')
            }
          }}
        />
      </Form.Group>

      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'transportAreaId'}
          options={transportationAreasList?.map(transportArea => ({ value: transportArea.id, text: transportArea.name })) || []}
          rules={{
            required: t('Please select transport area')
          }}
          label={t('Transportation Area')}
          placeholder={t('Please Transportation Area')}
        />
        <FormFields.Input
          control={control}
          name={'evsNumber'}
          label={t('EVS-Nr.')}
          placeholder={t('Please enter EVS-Nr.')}
          rules={{
            required: t('Please enter EVS-Nr.')
          }}
        />
        <FormFields.Input
          control={control}
          name={'agisId'}
          label={t('AGIS-ID')}
          placeholder={t('Please enter AGIS-ID')}
          rules={{
            required: t('Please enter AGIS-ID')
          }}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'languageId'}
          options={collectionSiteOptions?.languages?.map(language => ({ value: language.id, text: language.label, flag: language.icon })) || []}
          rules={{
            required: t('Please select language')
          }}
          label={t('Language')}
          placeholder={t('Please select language')}
        />
        <FormFields.Select
          control={control}
          name={'collectionMethodId'}
          options={collectionSiteOptions?.collectionMethods?.map(method => ({ value: method.id, text: method.label })) || []}
          label={t('Collection method')}
          placeholder={t('Please select collection method')}
        />
        <FormFields.Select
          control={control}
          name={'contractTypeId'}
          options={collectionSiteOptions?.contractTypes?.map(type => ({ value: type.id, text: type.label })) || []}
          label={t('Contract type')}
          placeholder={t('Please select contract type')}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'billingGroupId'}
          options={collectionSiteOptions?.billingGroups?.map(group => ({ value: group.id, text: group.name })) || []}
          label={t('Billing group')}
          placeholder={t('Please select billing group')}
        />
        <FormFields.Select
          control={control}
          name={'transportTariffId'}
          options={collectionSiteOptions?.tranportTariffs?.map(tariff => ({ value: tariff.id, text: tariff.name })) || []}
          label={t('Transport tariff')}
          placeholder={t('Please select transport tariff')}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'milkTypeId'}
          options={collectionSiteOptions?.milkTypes?.map(type => ({ value: type.id, text: type.name })) || []}
          rules={{
            required: t('Please select milk type')
          }}
          label={t('Milchtype')}
          placeholder={t('Please select milk type')}
        />
        <FormFields.Select
          control={control}
          name={'milkTypeCharacteristicId'}
          options={collectionSiteOptions?.milkTypeCharacteristics?.map(char => ({ value: char.id, text: char.label })) || []}
          label={t('Milk type Characteristic')}
          placeholder={t('Please select milk type characteristic')}
        />
      </Form.Group>
      <LocationForm
        countries={countries || []}
        collectionSiteOptions={collectionSiteOptions}
        useGoogleMapLocation
      />
    </>
  }

  async function onSubmit(data) {
    setIsBusy(true)
    try {
      if (id) {
        await DairyService.updateCollectionSite(id, data)
        notify(t('Successfully updated collection site'), 'success')
      } else {
        await DairyService.storeCollectionSite(data)
        notify(t('Successfully created collection site'), 'success')
      }
      navigate(RouteTypes.DAIRY_FACTORY_COLLECTION_SITE)
    } catch (errors) {
      handleErrors(errors)
    }
    setIsBusy(false)
  }

  const isLoading = isBusy || (id && isLoadingEdit) || isLoadingTransportAreas || isLoadingCollectionOptions || isLoadingCountries

  return <Layout
    title={id ? t('Edit collection site') : t('Create collection site')}
    onSubmit={methods.handleSubmit(onSubmit)}
    segmented
    isBusy={isLoading}
    close={RouteTypes.DAIRY_FACTORY_COLLECTION_SITE}
    action={id ? t('Update collection site') : t('Add collection site')}
  >
    <FormProvider {...methods}>
      {renderForm()}
    </FormProvider>
  </Layout>
}
export default CreateEditCollectionSites
